import { CountryExportOption } from "../../../schemas/exportSchema/form";

// Define the possible group dimensions for country options.
export type GroupDimension = "continent" | "idfRegion" | "whoRegion";

// Map of group dimension to human-readable label for display in the UI.
export const GROUP_DIMENSION_LABELS: Record<GroupDimension, string> = {
  continent: "Continent",
  idfRegion: "IDF region",
  whoRegion: "WHO region",
};

export function getGroupFieldValue(
  option: CountryExportOption,
  dimension: GroupDimension
): string | null {
  return option?.[dimension] ?? null;
}

/**
 * Filter options to only those that have a value for the given dimension.
 * For example, if the dimension is "idfRegion", only return countries that have a non-null `idfRegion` value.
 * @param options - The list of country options to filter.
 * @param dimension - The dimension to filter by (e.g., "continent", "idfRegion", "whoRegion").
 * @returns A filtered list of country options that have a value for the specified dimension.
 */
export function filterOptionsForDimension(
  options: CountryExportOption[],
  dimension: GroupDimension
): CountryExportOption[] {
  if (dimension !== "idfRegion" && dimension !== "whoRegion") return options;
  return options.filter((option) => getGroupFieldValue(option, dimension) != null);
}

export type GroupSelectionState = "checked" | "unchecked" | "indeterminate";
/**
 * Determine the selection state of a group of countries based on the selected countries and the countries in the group.
 * @param selected - The list of currently selected country location codes.
 * @param groupLocCodes - The list of country location codes in the group.
 * @returns The selection state of the group: "checked" if all countries in the group are selected, "unchecked" if none are selected, or "indeterminate" if some are selected.
 */
export function groupSelectionState(
  selected: number[],
  groupLocCodes: number[]
): GroupSelectionState {
  if (groupLocCodes.length === 0) return "unchecked";
  const selectedSet = new Set(selected);
  const selectedInGroup = groupLocCodes.filter((code) => selectedSet.has(code)).length;
  if (selectedInGroup === 0) return "unchecked";
  if (selectedInGroup === groupLocCodes.length) return "checked";
  return "indeterminate";
}

export type ToggleGroupResult = {
  nextSelected: number[]; // The updated list of selected country location codes after toggling the group.
  addedCount: number;
  skippedCount: number;
  removed: boolean;
};

/**
 * Toggle all countries in a group. When adding, respects `max` and may only add a subset.
 * @param selected - The list of currently selected country location codes.
 * @param groupLocCodes - The list of country location codes in the group.
 * @param max - The maximum number of countries that can be selected.
 * @returns The result of the toggle operation, including the next selected list, counts of added and skipped countries, and whether any were removed.
 */
export function toggleGroup(
  selected: number[],
  groupLocCodes: number[],
  max: number
): ToggleGroupResult {
  const selectedSet = new Set(selected);
  const state = groupSelectionState(selected, groupLocCodes);

  // User is unchecking the group, so remove all groupLocCodes from selected.
  if (state === "checked") {
    const removeSet = new Set(groupLocCodes);
    return {
      nextSelected: selected.filter((code) => !removeSet.has(code)),
      addedCount: 0,
      skippedCount: 0,
      removed: true,
    };
  }

  // User is checking the group, so add all groupLocCodes to selected, respecting max.
  const toAdd = groupLocCodes.filter((code) => !selectedSet.has(code));
  const remaining = Math.max(0, max - selected.length);
  const added = toAdd.slice(0, remaining);
  const skippedCount = toAdd.length - added.length;

  return {
    nextSelected: [...selected, ...added],
    addedCount: added.length,
    skippedCount,
    removed: false,
  };
}
